/**
 * Real Platform reads used by the Enterprise Command Center (Stage F5)
 * — the first stage to need Users/Hospitals/Branches/Roles/Settings/
 * License beyond `platform-directory.service.ts`'s own narrower
 * Department/Branch selectors (Stage F2). Every endpoint here is a
 * real, certified `GET` — see `ENTERPRISE-DASHBOARD-BACKEND-MAPPING.md`
 * §2 for the full audit.
 */
import { httpClient, unwrap, API_ENDPOINTS } from '@core/api';
import { ApiError, type ApiSuccessResponse } from '@app-types/api.types';
import type { UserDto, HospitalDto, BranchDto, RoleDto, LicenseDto, ShiftDto } from '@nexao/contracts';
import type { OrganizationSettingsInput, SystemSettingsInput, LocalizationSettingsInput } from '@nexao/contracts';

export async function listUsers(): Promise<UserDto[]> {
  return unwrap(httpClient.get<ApiSuccessResponse<UserDto[]>>(API_ENDPOINTS.platform.users, { params: { page: 1, limit: 100 } }));
}

export async function listHospitals(): Promise<HospitalDto[]> {
  return unwrap(httpClient.get<ApiSuccessResponse<HospitalDto[]>>(API_ENDPOINTS.platform.hospitals, { params: { page: 1, limit: 100 } }));
}

export async function listBranches(hospitalId: string): Promise<BranchDto[]> {
  return unwrap(
    httpClient.get<ApiSuccessResponse<BranchDto[]>>(API_ENDPOINTS.platform.branches, {
      params: { page: 1, limit: 100, hospitalId },
    }),
  );
}

/**
 * `GET /branches` genuinely requires `hospitalId` server-side (still
 * hospital-scoped, unlike Department's own tenant-wide rebuild) — this
 * fetches every hospital first, then every branch under each, for
 * screens that just need "any branch in this tenant" (e.g. a Home
 * Branch/Default Branch dropdown), without pretending `listBranches()`
 * has an optional param the backend doesn't actually accept.
 */
export async function listAllBranchesAcrossHospitals(): Promise<BranchDto[]> {
  const hospitals = await listHospitals();
  const branchesPerHospital = await Promise.all(hospitals.map((hospital) => listBranches(hospital.id)));
  return branchesPerHospital.flat();
}

export async function listRoles(): Promise<RoleDto[]> {
  return unwrap(httpClient.get<ApiSuccessResponse<RoleDto[]>>(API_ENDPOINTS.platform.roles, { params: { page: 1, limit: 100 } }));
}

/** Enterprise Employee Master rebuild — Default Shift selector. First frontend consumer of this already-mounted, already-permissioned backend endpoint. */
export async function listShifts(): Promise<ShiftDto[]> {
  return unwrap(httpClient.get<ApiSuccessResponse<ShiftDto[]>>(API_ENDPOINTS.platform.shifts, { params: { page: 1, limit: 100 } }));
}

export async function getOrganizationSettings(): Promise<OrganizationSettingsInput> {
  return unwrap(httpClient.get<ApiSuccessResponse<OrganizationSettingsInput>>(API_ENDPOINTS.platform.organizationSettings));
}

export async function getSystemSettings(): Promise<SystemSettingsInput> {
  return unwrap(httpClient.get<ApiSuccessResponse<SystemSettingsInput>>(API_ENDPOINTS.platform.systemSettings));
}

export async function getLocalizationSettings(): Promise<LocalizationSettingsInput> {
  return unwrap(httpClient.get<ApiSuccessResponse<LocalizationSettingsInput>>(API_ENDPOINTS.platform.localizationSettings));
}

/**
 * The tenant's licence, or `null` when it has none.
 *
 * ABSENCE IS AN ANSWER, NOT A FAILURE. `GET /licenses/current` 404s when no
 * licence row exists, which is correct HTTP and a perfectly ordinary state — a
 * tenant that has never had one recorded. But a rejected promise made the
 * Configuration widget's normal "No license record" reading arrive as a red
 * console error on every dashboard load, which is exactly the kind of standing
 * false alarm that teaches people to ignore the console.
 *
 * So the 404 — and ONLY the 404 — is translated into `null`. Every other
 * failure still throws: a 403 means the caller lacks `license:read` and a 500
 * means the lookup broke, and reporting either as "no licence" would hide a
 * real problem behind a plausible answer.
 */
export async function getLicense(): Promise<LicenseDto | null> {
  try {
    return await unwrap(httpClient.get<ApiSuccessResponse<LicenseDto>>(`${API_ENDPOINTS.platform.license}/current`));
  } catch (error) {
    if (error instanceof ApiError && error.statusCode === 404) return null;
    throw error;
  }
}
